import { fetchLabModalPopups } from 'actions/SystemActions'
import { ModalStackIdleContext, TryShowModalContext } from 'contexts/ModalProvider'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useLabModalPopupsRequestContext } from 'hooks/useLabRouteRequestContext'
import { getFirstEligibleLabItemByRank } from 'lib/lab/rankedLabItems'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import type React from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { recordModalPopupSeen, isModalPopupSuppressed } from 'storage/modalPopup'

function getRenderableModalPopup(modalPopups: App.LabModalPopupsResult | undefined): App.LabModalPopupResult | undefined {
  return getFirstEligibleLabItemByRank(modalPopups?.popups, popup => !isModalPopupSuppressed(popup))
}

export default function ModalPopupsGate() {
  const dispatch = useAppDispatch()
  const tryShowModal = useContext(TryShowModalContext)
  const isModalStackIdle = useContext(ModalStackIdleContext)
  const { currentPath, distinctId, token, labRequestContextKey } = useLabModalPopupsRequestContext()
  const modalPopups = useAppSelector(state => state.system.features.modalPopups)
  const modalPopupsRequestPath = useAppSelector(state => state.system.features.modalPopupsRequestPath)
  const shownPopupIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    dispatch(fetchLabModalPopups(currentPath, distinctId, token))
  }, [dispatch, currentPath, distinctId, token, labRequestContextKey])

  const popup = useMemo(() => {
    if (!isModalStackIdle) {
      return undefined
    }
    if (modalPopupsRequestPath && modalPopupsRequestPath !== currentPath) {
      return undefined
    }
    return getRenderableModalPopup(modalPopups)
  }, [isModalStackIdle, modalPopups, modalPopupsRequestPath, currentPath])

  const handleActionClick = useCallback((_event: React.MouseEvent<HTMLButtonElement>, resolve: (result?: unknown) => void) => {
    resolve()
  }, [])

  useEffect(() => {
    if (!isModalStackIdle || !popup || shownPopupIdsRef.current.has(popup.id)) {
      return
    }

    const secondaryCta = popup.content.primaryCta ? popup.content.secondaryCta : undefined

    const modalPromise = tryShowModal(() => {
      recordModalPopupSeen(popup)
      return (
        <Modal
          title={popup.content.title}
          image={popup.content.imageId ? { id: popup.content.imageId, title: popup.content.title } : undefined}
          imageParams={popup.content.imageId ? { mobileAspectRatio: '16:9', tabletAspectRatio: '16:9', desktopAspectRatio: '16:9' } : undefined}
          primaryActionText={popup.content.primaryCta?.label}
          primaryActionTo={popup.content.primaryCta?.path}
          secondaryActionText={secondaryCta?.label}
          secondaryActionTo={secondaryCta?.path}
          onPrimaryActionClick={handleActionClick}
          onSecondaryActionClick={handleActionClick}
          data-testid="lab-modal-popup"
        >
          {popup.content.description ? <BodyText variant="medium">{popup.content.description}</BodyText> : null}
        </Modal>
      )
    })

    if (!modalPromise) {
      return
    }

    shownPopupIdsRef.current.add(popup.id)
    modalPromise.then(() => undefined)
  }, [isModalStackIdle, popup, tryShowModal, handleActionClick])

  return null
}
